import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, MapPin, Route, ShieldCheck } from "lucide-react";
import { getMyCheckInHistoryById } from "../services/api";
import "../styles/SaleDetails.css";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-MY", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatTimeOnly = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDistance = (value, t) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return t("se.history.visitDistanceUnavailable");
  }

  if (numericValue >= 1000) {
    const km = numericValue / 1000;
    return `${km >= 10 ? km.toFixed(0) : km.toFixed(1)} km`;
  }

  return `${Math.round(numericValue)} m`;
};

const getAttendanceEventLabel = (type, t) => {
  switch (type) {
    case "clock_in":
      return t("se.history.attendance.clockIn");
    case "break_out":
      return t("se.history.attendance.breakOut");
    case "break_in":
      return t("se.history.attendance.breakIn");
    case "clock_out":
      return t("se.history.attendance.clockOut");
    default:
      return t("se.history.attendance.recordedAt");
  }
};

function VisitDetails() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionGroupId: sessionGroupIdParam } = useParams();
  const { visitData, previousView } = location.state || {};

  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sessionGroupId = Number.parseInt(
    sessionGroupIdParam || visitData?.session_group_id || visitData?.check_in_id,
    10,
  );

  useEffect(() => {
    if (!Number.isInteger(sessionGroupId) || sessionGroupId <= 0) {
      setError(
        t("se.history.visitDetails.noVisitData", {
          defaultValue: "No visit data found.",
        }),
      );
      setLoading(false);
      return;
    }

    const fetchVisit = async () => {
      try {
        setLoading(true);
        const response = await getMyCheckInHistoryById(sessionGroupId);
        if (response?.success && response?.data) {
          setVisit(response.data);
        } else {
          setError(
            t("se.history.visitDetails.errorFetch", {
              defaultValue: "Failed to fetch visit details.",
            }),
          );
        }
      } catch (fetchError) {
        setError(
          fetchError?.response?.data?.error ||
            t("se.history.visitDetails.errorFetch", {
              defaultValue: "Failed to fetch visit details.",
            }),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchVisit();
  }, [sessionGroupId, t]);

  const attendanceEvents = useMemo(
    () =>
      (Array.isArray(visit?.attendance_events) ? visit.attendance_events : []).map(
        (event) => ({
          ...event,
          label: getAttendanceEventLabel(event?.type, t),
        }),
      ),
    [t, visit?.attendance_events],
  );

  const goBack = () =>
    navigate("/promoter", {
      state: { view: previousView || "HISTORY" },
    });

  if (loading) {
    return (
      <div className="sale-details-container">
        <div className="sale-details-skeleton-card" aria-hidden="true">
          <div className="sale-details-skeleton-header"></div>
          <div className="sale-details-skeleton-line sale-details-skeleton-line-lg"></div>
          <div className="sale-details-skeleton-grid">
            <div className="sale-details-skeleton-panel"></div>
            <div className="sale-details-skeleton-panel"></div>
            <div className="sale-details-skeleton-panel"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !visit) {
    return (
      <div className="sale-details-container">
        <div className="error-state">
          <p>{error || t("se.history.visitDetails.noVisitData", { defaultValue: "No visit data found." })}</p>
          <button onClick={goBack} className="back-button">
            <ArrowLeft size={16} />
            {t("se.saleDetails.goBack")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sale-details-container">
      <div className="se-header">
        <div className="se-header-title">
          <button onClick={goBack} className="se-back-btn">
            <ArrowLeft size={24} />
          </button>
          <span className="sale-title">
            {t("se.history.visitDetails.title", {
              id: visit.session_group_id || visit.check_in_id,
              defaultValue: "Visit #{{id}}",
            })}
          </span>
        </div>
      </div>

      <div className="sale-details-content">
        <div className="sale-info-card">
          <div className="sale-info-header">
            <div className="sale-date">
              <Calendar size={16} />
              <span>{formatDateTime(visit.checked_in_at)}</span>
            </div>
          </div>

          <div className="sale-info-body">
            <div className="sale-info-panel sale-info-panel-highlight">
              <div className="sale-info-panel-icon">
                <MapPin size={18} />
              </div>
              <div className="sale-info-panel-content">
                <div className="sale-info-panel-label">
                  {t("se.history.outlet")}
                </div>
                <div className="sale-info-panel-value">
                  {visit.outlet_name || t("se.displayProof.unknownOutlet")}
                </div>
                <div className="sale-info-panel-subvalue">
                  {visit.outlet_address || t("se.history.visitNoAddress")}
                </div>
              </div>
            </div>

            <div className="sale-info-panel">
              <div className="sale-info-panel-icon">
                <ShieldCheck size={18} />
              </div>
              <div className="sale-info-panel-content">
                <div className="sale-info-panel-label">
                  {t("se.history.visitDetails.locationStatus", {
                    defaultValue: "Location Status",
                  })}
                </div>
                <div className="sale-info-panel-value sale-info-panel-value-muted">
                  {String(visit.location_match_status || "")
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (char) => char.toUpperCase()) || t("se.history.visitStatus.unverified")}
                </div>
              </div>
            </div>

            <div className="sale-info-panel">
              <div className="sale-info-panel-icon">
                <Route size={18} />
              </div>
              <div className="sale-info-panel-content">
                <div className="sale-info-panel-label">
                  {t("se.history.visitDetails.distanceFromOutlet", {
                    defaultValue: "Distance From Outlet",
                  })}
                </div>
                <div className="sale-info-panel-value sale-info-panel-value-muted">
                  {formatDistance(visit.distance_from_outlet_meters, t)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sale-items-card">
          <div className="items-header">
            <Clock size={18} />
            <h3>
              {t("se.history.attendance.title")}
            </h3>
          </div>
          {attendanceEvents.length > 0 ? (
            <div className="history-visit-attendance history-visit-attendance--detail">
              <div className="history-visit-attendance-grid">
                {attendanceEvents.map((event, index) => (
                  <div
                    key={`visit-attendance-${event.type}-${index}`}
                    className="history-visit-attendance-item"
                  >
                    <span className="history-visit-attendance-label">
                      {event.label}
                    </span>
                    <span className="history-visit-attendance-time">
                      {formatTimeOnly(event.at)}
                    </span>
                    <span className="sale-info-panel-subvalue">
                      {formatDateTime(event.at)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="no-items">
              {t("se.history.visitDetails.noAttendanceEvents", {
                defaultValue: "No attendance events found for this visit.",
              })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default VisitDetails;
